import { startTransition, useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import InsertAssetModal from '@/components/modals/InsertAssetModal';
import AssignCampaignModal from '@/components/modals/AssignCampaignModal';
import { getCreative, updateCreative } from '@/services/creatives';
import { listAssets, getAsset } from '@/services/assets';
import { formatPathIdFromStored, getTemplateInFormat } from '@/services/formats';
import { listCreativeRenders } from '@/services/renders';
import type { Creative as CreativeType, Asset, Template, RenderJobSummary } from '@/types/api';
import {
  asRecord,
  buildWorkingElements,
  deepClone,
  TemplateDrivenCanvas,
  type AssetFileShape,
} from '@/creativeComposition';
import { RenderJobList } from '@/components/app/RenderJobList';
import StartRenderModal from '@/components/modals/StartRenderModal';
import { IconButton } from '@/components/primitives/IconButton';
import { RveCreativeEditorPage } from '@/pages/RveCreativeEditorPage';

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function CreativeEditorPage() {
  const { campaignId: campaignIdParam, creativeId: creativeIdParam } = useParams();
  const navigate = useNavigate();

  const campaignId = campaignIdParam != null ? parseInt(campaignIdParam, 10) : undefined;
  const creativeId = creativeIdParam != null ? parseInt(creativeIdParam, 10) : undefined;
  const isInvalidId = creativeId === undefined || Number.isNaN(creativeId);

  const [creative, setCreative] = useState<CreativeType | null>(null);
  const [template, setTemplate] = useState<Template | null>(null);
  const [workingElements, setWorkingElements] = useState<unknown[]>([]);
  const [assetsList, setAssetsList] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Rename state
  const [nameEditing, setNameEditing] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);

  // Modal state
  const [assetModalOpen, setAssetModalOpen] = useState(false);
  const [assetTargetIndex, setAssetTargetIndex] = useState<number | null>(null);
  const [insertAssetType, setInsertAssetType] = useState<'image' | 'video' | null>(null);
  const [assignModalOpen, setAssignModalOpen] = useState(false);

  // Renders state
  const [renderModalOpen, setRenderModalOpen] = useState(false);
  const [rendersPanelOpen, setRendersPanelOpen] = useState(false);
  const [creativeRenders, setCreativeRenders] = useState<RenderJobSummary[]>([]);
  const [rendersLoading, setRendersLoading] = useState(false);
  const [rendersError, setRendersError] = useState('');

  // ── Load creative + template + assets ──
  useEffect(() => {
    if (isInvalidId) return;
    startTransition(() => {
      setLoading(true);
      setError('');
    });
    getCreative(creativeId!)
      .then((c) => {
        setCreative(c);
        return getTemplateInFormat(formatPathIdFromStored(c.format_id), c.template_id).then((t) => {
          setTemplate(t);
          setWorkingElements(buildWorkingElements(t, c.elements));
        });
      })
      .then(() => listAssets().then(setAssetsList))
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load creative'))
      .finally(() => setLoading(false));
  }, [creativeId, isInvalidId]);

  // ── Patch a working element immutably ──
  const patchEl = useCallback((index: number, fn: (el: Record<string, unknown>) => void) => {
    setWorkingElements((prev) => {
      const copy = deepClone(prev) as Record<string, unknown>[];
      fn(copy[index]);
      return copy;
    });
  }, []);

  // ── Handle asset selection from modal ──
  const handleAssetSelect = useCallback(
    async (assetId: number) => {
      if (assetTargetIndex == null || template == null) return;
      const el = asRecord(template.elements[assetTargetIndex]);
      const cur = asRecord(workingElements[assetTargetIndex]);
      const typ = String(cur?.element_type ?? el?.element_type ?? '');
      let asset = assetsList.find((a) => a.id === assetId);
      if (!asset) {
        try {
          const fetched = await getAsset(assetId);
          asset = fetched;
          if (fetched.id != null) {
            setAssetsList((prev) => {
              const without = prev.filter((a) => a.id !== fetched.id);
              return [fetched, ...without];
            });
          }
        } catch (err: unknown) {
          setError(err instanceof Error ? err.message : 'Failed to load selected asset');
          return;
        }
      }
      if (!asset || asset.id == null) return;
      if (typ === 'image' && asset.asset_type !== 'image') return;
      if (typ === 'video' && asset.asset_type !== 'video') return;
      const af: AssetFileShape = {
        uri: asset.uri,
        name: asset.name,
        asset_type: asset.asset_type,
        organization_id: asset.organization_id,
      };
      patchEl(assetTargetIndex, (row) => {
        const data = asRecord(row.element_data) ?? {};
        if (typ === 'image') {
          const ie = asRecord(data.image_element) ?? {};
          ie.asset_file = af;
          data.image_element = ie;
        } else if (typ === 'video') {
          const ve = asRecord(data.video_element) ?? {};
          ve.asset_file = af;
          data.video_element = ve;
        }
        row.element_data = data;
      });
      setAssetTargetIndex(null);
    },
    [assetTargetIndex, assetsList, patchEl, template, workingElements],
  );

  const handleAssetCreated = useCallback((asset: Asset) => {
    if (asset.id == null) return;
    setAssetsList((prev) => {
      const without = prev.filter((a) => a.id !== asset.id);
      return [asset, ...without];
    });
  }, []);

  // ── Save elements to API ──
  const handleSave = useCallback(() => {
    if (!creative?.id || isInvalidId) return;
    setSaving(true);
    setError('');
    updateCreative(creative.id, { elements: workingElements })
      .then(setCreative)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to save'))
      .finally(() => setSaving(false));
  }, [creative, isInvalidId, workingElements]);

  // ── Rename creative ──
  const handleRename = useCallback(
    (name: string) => {
      if (!creative?.id) return;
      updateCreative(creative.id, { name })
        .then((updated) => {
          setCreative(updated);
          setNameEditing(false);
          setNameError(null);
        })
        .catch((err) => setNameError(err instanceof Error ? err.message : 'Rename failed'));
    },
    [creative],
  );

  // ── Renders history ──
  const fetchCreativeRenders = useCallback(() => {
    if (creativeId == null || Number.isNaN(creativeId)) return;
    startTransition(() => {
      setRendersLoading(true);
      setRendersError('');
    });
    listCreativeRenders(creativeId)
      .then(setCreativeRenders)
      .catch((err) =>
        setRendersError(err instanceof Error ? err.message : 'Failed to load renders'),
      )
      .finally(() => setRendersLoading(false));
  }, [creativeId]);

  useEffect(() => {
    if (!rendersPanelOpen || creativeId == null || Number.isNaN(creativeId)) return;
    fetchCreativeRenders();
  }, [rendersPanelOpen, creativeId, fetchCreativeRenders]);

  useEffect(() => {
    if (!rendersPanelOpen) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setRendersPanelOpen(false);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [rendersPanelOpen]);

  // ── Error / loading states ──
  if (isInvalidId) {
    return (
      <div className="p-8">
        <p className="text-error font-medium">Invalid creative ID.</p>
        <Link
          to="/creatives"
          className="mt-4 inline-flex items-center gap-2 text-primary font-bold text-sm hover:underline"
        >
          <span className="material-symbols-outlined text-lg">arrow_back</span>
          Back to Creatives
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[60vh]">
        <span className="material-symbols-outlined animate-spin text-4xl text-primary">
          progress_activity
        </span>
      </div>
    );
  }

  if (!creative || !template) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-4 p-8">
        <span className="material-symbols-outlined text-5xl text-error/50">error</span>
        <p className="text-on-surface-variant">{error || 'Creative not found.'}</p>
        <Link to="/creatives" className="text-primary font-bold text-sm hover:underline">
          ← Back to Creatives
        </Link>
      </div>
    );
  }

  if (creative.rve_creative) {
    return (
      <RveCreativeEditorPage
        creative={creative}
        template={template}
        creativeId={creativeId!}
        campaignId={campaignId}
        onCreativeUpdated={setCreative}
      />
    );
  }

  const unassigned = !creative.assigned_campaign_ids?.length;

  return (
    // Full viewport minus the AppShell header (4rem = 64px) — editor fills the rest
    <div className="flex flex-col" style={{ height: 'calc(100vh - 4rem)' }}>
      {/* ── Editor Top Bar ── */}
      <div className="shrink-0 bg-surface-container-lowest border-b border-outline-variant/10 px-6 py-3 flex items-center justify-between gap-4 z-10">
        <div className="flex items-center gap-4 min-w-0 flex-1">
          {/* Back button */}
          <button
            type="button"
            onClick={() => navigate(campaignId != null ? `/campaigns/${campaignId}` : '/creatives')}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-on-surface-variant hover:bg-surface-container hover:text-on-surface transition-all text-sm font-bold cursor-pointer shrink-0"
          >
            <span className="material-symbols-outlined text-[18px]">arrow_back</span>
            <span className="hidden sm:inline">
              {campaignId != null ? 'Campaign' : 'Creatives'}
            </span>
          </button>

          {/* Creative name / inline rename */}
          {nameEditing ? (
            <div className="flex items-center gap-2 min-w-0 max-w-sm flex-wrap">
              <input
                autoFocus
                value={nameValue}
                onChange={(e) => setNameValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleRename(nameValue.trim() || creative.name);
                  if (e.key === 'Escape') {
                    setNameEditing(false);
                    setNameError(null);
                  }
                }}
                className="flex-1 min-w-0 px-3 py-1.5 rounded-lg border border-primary/40 bg-surface-container-low text-on-surface text-sm font-bold focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
              <button
                onClick={() => handleRename(nameValue.trim() || creative.name)}
                className="p-1.5 text-primary cursor-pointer"
                aria-label="Save name"
              >
                <span className="material-symbols-outlined text-lg">check</span>
              </button>
              <button
                onClick={() => {
                  setNameEditing(false);
                  setNameError(null);
                }}
                className="p-1.5 text-on-surface-variant cursor-pointer"
                aria-label="Cancel"
              >
                <span className="material-symbols-outlined text-lg">close</span>
              </button>
              {nameError && <p className="w-full text-xs text-error">{nameError}</p>}
            </div>
          ) : (
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-headline font-bold text-on-surface truncate">
                {creative.name}
              </span>
              <button
                onClick={() => {
                  setNameValue(creative.name);
                  setNameEditing(true);
                }}
                className="p-1 text-on-surface-variant hover:text-on-surface cursor-pointer shrink-0"
                aria-label="Rename creative"
              >
                <span className="material-symbols-outlined text-lg">edit</span>
              </button>
            </div>
          )}

          {/* Template badge */}
          <span className="hidden lg:flex items-center gap-1.5 px-2.5 py-1 bg-surface-container rounded-lg text-xs font-bold text-on-surface-variant border border-outline-variant/20 shrink-0">
            <span className="material-symbols-outlined text-[14px]">tv</span>
            {template.name}
          </span>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => setRendersPanelOpen((o) => !o)}
            className={`flex items-center gap-1.5 rounded-xl h-9 px-4 text-sm font-bold transition-colors cursor-pointer ${
              rendersPanelOpen
                ? 'bg-primary-container text-primary border border-primary/20'
                : 'border border-outline-variant/30 text-on-surface-variant hover:bg-surface-container hover:text-on-surface'
            }`}
          >
            <span className="material-symbols-outlined text-lg">history</span>
            <span className="hidden xl:inline">History</span>
          </button>

          <button
            type="button"
            onClick={() => setRenderModalOpen(true)}
            className="flex items-center gap-1.5 rounded-xl h-9 px-4 border border-primary/60 text-primary bg-primary/5 hover:bg-primary/10 transition-colors text-sm font-bold cursor-pointer"
          >
            <span className="material-symbols-outlined text-[17px]">movie</span>
            <span className="hidden sm:inline">Export</span>
          </button>

          <div className="w-[1px] h-6 bg-outline-variant/30 mx-1 hidden md:block" />

          {unassigned && (
            <button
              type="button"
              onClick={() => setAssignModalOpen(true)}
              className="flex items-center gap-1.5 rounded-xl h-9 px-4 border border-outline-variant/30 text-on-surface-variant text-sm font-bold hover:bg-surface-container hover:text-on-surface transition-colors cursor-pointer"
            >
              <span className="material-symbols-outlined text-lg">link</span>
              <span className="hidden sm:inline">Assign Campaign</span>
            </button>
          )}
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1.5 rounded-xl h-9 px-5 bg-primary text-on-primary text-sm font-bold hover:bg-primary-dim transition-colors cursor-pointer disabled:opacity-60"
          >
            {saving ? (
              <>
                <span className="material-symbols-outlined text-lg animate-spin">
                  progress_activity
                </span>
                <span className="hidden sm:inline">Saving…</span>
              </>
            ) : (
              <>
                <span className="material-symbols-outlined text-lg">save</span>
                Save
              </>
            )}
          </button>
        </div>
      </div>

      {/* ── Canvas Row ── */}
      <div className="flex-1 min-h-0 relative overflow-x-hidden flex border-t border-outline-variant/10">
        <div className="flex-1 min-w-0 min-h-0 flex flex-col bg-surface-container-low">
          <TemplateDrivenCanvas
            template={template}
            workingElements={workingElements}
            assetsList={assetsList}
            patchEl={patchEl}
            onPickImage={(idx) => {
              setAssetTargetIndex(idx);
              setInsertAssetType('image');
              setAssetModalOpen(true);
            }}
            onPickVideo={(idx) => {
              setAssetTargetIndex(idx);
              setInsertAssetType('video');
              setAssetModalOpen(true);
            }}
          />
        </div>

        {/* Sidebar panel for renders */}
        {rendersPanelOpen && (
          <div
            className="absolute inset-0 z-40 bg-inverse-surface/30 backdrop-blur-sm xl:hidden"
            onClick={() => setRendersPanelOpen(false)}
          />
        )}
        <aside
          className={`absolute top-0 right-0 bottom-0 z-50 flex w-full max-w-md flex-col border-l border-outline-variant/20 bg-surface-container-lowest shadow-2xl transition-transform duration-300 ease-in-out xl:relative xl:shadow-none ${
            rendersPanelOpen
              ? 'translate-x-0 pointer-events-auto xl:w-[32rem]'
              : 'translate-x-full pointer-events-none xl:hidden xl:w-0 border-l-0'
          }`}
        >
          {rendersPanelOpen && (
            <>
              <div className="shrink-0 flex items-center justify-between gap-3 px-5 py-4 border-b border-outline-variant/10 bg-surface-container-lowest">
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="material-symbols-outlined text-primary shrink-0 text-xl">
                    history
                  </span>
                  <span className="text-sm font-headline font-bold text-on-surface truncate">
                    Render History
                  </span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <IconButton
                    icon="refresh"
                    size="sm"
                    variant="ghost"
                    onClick={() => fetchCreativeRenders()}
                  />
                  <IconButton
                    icon="close"
                    size="sm"
                    variant="ghost"
                    onClick={() => setRendersPanelOpen(false)}
                  />
                </div>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto p-5 bg-surface-container-lowest">
                {rendersError && (
                  <div className="mb-4 p-3 rounded-lg bg-error-container text-on-error-container text-sm font-medium">
                    {rendersError}
                  </div>
                )}
                {rendersLoading ? (
                  <div className="flex justify-center py-16">
                    <span className="material-symbols-outlined animate-spin text-3xl text-primary">
                      progress_activity
                    </span>
                  </div>
                ) : (
                  <RenderJobList
                    rows={creativeRenders}
                    emptyMessage="No renders for this creative yet. Use Export to start."
                  />
                )}
              </div>
            </>
          )}
        </aside>
      </div>

      {/* ── Footer ── */}
      <footer className="shrink-0 border-t border-outline-variant/10 bg-surface-container-lowest px-6 py-3 flex items-center justify-between gap-4 text-xs text-on-surface-variant">
        <div className="flex items-center gap-2">
          <span className="material-symbols-outlined text-base">aspect_ratio</span>
          <span>Canvas scales with viewport. Export resolutions are a future render step.</span>
        </div>
        <span className="font-medium">{template.name}</span>
      </footer>

      {/* ── Modals ── */}
      <InsertAssetModal
        isOpen={assetModalOpen}
        onClose={() => {
          setAssetModalOpen(false);
          setAssetTargetIndex(null);
          setInsertAssetType(null);
        }}
        onSelect={handleAssetSelect}
        onAssetCreated={handleAssetCreated}
        allowedAssetType={insertAssetType ?? undefined}
      />
      <AssignCampaignModal
        isOpen={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        creativeId={creative.id ?? null}
        creativeName={creative.name}
        onAssigned={() => {
          getCreative(creativeId!)
            .then(setCreative)
            .catch((err: unknown) =>
              setError(
                err instanceof Error ? err.message : 'Failed to refresh creative after assignment',
              ),
            );
        }}
      />
      {creative.id != null && (
        <StartRenderModal
          isOpen={renderModalOpen}
          onClose={() => setRenderModalOpen(false)}
          creativeId={creative.id}
          creativeName={creative.name}
          onStarted={() => {
            if (rendersPanelOpen) fetchCreativeRenders();
          }}
        />
      )}
    </div>
  );
}
